/** Helpers para respuesta agrupada de /getmylots (lotes con units[]). */

import { pickLotContextFields } from './lotContextFields';
import { mapOperationFromApi } from './userLotFormUtils';

export const getUnitListIds = (unit) => {
  const idsFromLists = Array.isArray(unit?.lists) ? unit.lists.map((x) => x?.id).filter(Boolean) : [];
  const idsFromListIds = Array.isArray(unit?.list_ids) ? unit.list_ids.filter(Boolean) : [];
  const legacyId = unit?.list_id ? [unit.list_id] : [];
  return Array.from(new Set([...idsFromLists, ...idsFromListIds, ...legacyId]));
};

export const getDisplayUnits = (lot) => (Array.isArray(lot?.units) ? lot.units : []);

/**
 * Fila a abrir cuando el visitante clickea pin/parcela en el mapa compartido.
 * - 0 UF → ítem de lote (catastro / contenido de lote de la lista).
 * - 1 UF → esa UF (paridad con click de card de una sola unidad).
 * - N>1 → contexto de lote sin UF seleccionada (`item_type:'units_list'`) para que el
 *   dock abra el tab Unidades en modo lista. El click de card no usa este helper.
 * No forzar item_type:'lot' cuando hay UFs: eso abre solo catastro y saltea la publicación.
 */
export const buildSharedMapOpenSelection = (lotId, groupedLots = []) => {
  if (lotId == null || lotId === '') return null;
  const group = groupedLots.find((lot) => String(lot.lot_id) === String(lotId));
  if (!group) return null;
  const displayUnits = getDisplayUnits(group);
  if (!displayUnits.length) {
    return {
      ...group,
      lot_id: group.lot_id,
      item_type: 'lot',
      user_lot_id: null,
      id: null,
    };
  }
  if (displayUnits.length === 1) {
    const targetUnit = displayUnits[0];
    return {
      ...targetUnit,
      lot_id: targetUnit.lot_id ?? group.lot_id,
      item_type: 'unit',
      user_lot_id: targetUnit.user_lot_id ?? targetUnit.id,
    };
  }
  return {
    ...group,
    lot_id: group.lot_id,
    item_type: 'units_list',
    user_lot_id: null,
    id: null,
  };
};


/** Lote agregado a lista sin unidades funcionales (units: []). */
export const isDirectLot = (lot) => getDisplayUnits(lot).length === 0;

export const getLotListIds = (lot) => {
  const idsFromLists = Array.isArray(lot?.lists) ? lot.lists.map((x) => x?.id).filter(Boolean) : [];
  const idsFromListIds = Array.isArray(lot?.list_ids) ? lot.list_ids.filter(Boolean) : [];
  const legacyId = lot?.list_id ? [lot.list_id] : [];
  return Array.from(new Set([...idsFromLists, ...idsFromListIds, ...legacyId]));
};

/** Estado de membresía mixta de una lista a partir de filas agrupadas de /getmylots. */
export const extractListMembership = (lots) => {
  const lot_ids = [];
  const user_lot_ids = [];

  (Array.isArray(lots) ? lots : []).forEach((lot) => {
    const units = getDisplayUnits(lot);
    if (units.length === 0) {
      if (lot?.lot_id) lot_ids.push(lot.lot_id);
      return;
    }
    units.forEach((unit) => {
      const unitId = unit?.id || unit?.user_lot_id;
      if (unitId) user_lot_ids.push(unitId);
    });
  });

  return {
    lot_ids: Array.from(new Set(lot_ids)),
    user_lot_ids: Array.from(new Set(user_lot_ids)),
  };
};

const toOptionalString = (value) => {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
};

/** Título visible en vistas compartidas (sin etiqueta interna). */
export const getUnitPublicTitle = (unit) => {
  const title = toOptionalString(unit?.title);
  return title || 'Sin título';
};

/**
 * Título del header de la ficha (editor): prioriza el título de publicación;
 * la etiqueta interna solo si no hay título; después UF {n} o el alta.
 */
export const getUnitHeaderTitle = (form, officialUnit) => {
  const title = toOptionalString(form?.title);
  if (title) return title;
  const label = toOptionalString(form?.unit_label);
  if (label) return label;
  return officialUnit?.unit_number ? `UF ${officialUnit.unit_number}` : 'CREAR UNIDAD FUNCIONAL';
};

export const getUnitDisplayTitle = (unit) => {
  const label = toOptionalString(unit?.unit_label);
  const title = toOptionalString(unit?.title);
  if (label && title) return `${label} · ${title}`;
  return label || title || 'Sin título';
};

/**
 * Filas del desplegable (Registros y detalle de lista). La etiqueta interna
 * es lo que se escanea; el título de publicación va debajo, y solo si dice
 * otra cosa. No se concatenan en una sola línea.
 * @returns {{ headline: string, subtitle: string }}
 */
export const getUnitListLines = (unit) => {
  const label = toOptionalString(unit?.unit_label);
  const title = toOptionalString(unit?.title);
  if (label && title && label.localeCompare(title, 'es', { sensitivity: 'accent' }) !== 0) {
    return { headline: label, subtitle: title };
  }
  return { headline: label || title || 'Sin título', subtitle: '' };
};

/** Etiquetas de listas activas en el filtro a las que pertenece una unidad. */
export const getUnitMatchingListLabels = (unit, listFilters, userLists = [], noListsValue = '__NO_LISTS__') => {
  const sel = Array.isArray(listFilters) ? listFilters.filter(Boolean) : [];
  if (!sel.length || !unit) return [];

  const unitListIds = getUnitListIds(unit);
  const hasLegacyListNameOnly = !unitListIds.length && !!unit?.list_name;
  const isUnlisted = !unitListIds.length && !hasLegacyListNameOnly;
  const labels = [];

  if (sel.includes(noListsValue) && isUnlisted) {
    labels.push({ id: noListsValue, name: 'Sin lista' });
  }

  sel.filter((id) => id !== noListsValue).forEach((listId) => {
    if (!unitListIds.includes(listId)) return;
    const fromUnit = Array.isArray(unit.lists) ? unit.lists.find((l) => l?.id === listId) : null;
    const fromCatalog = userLists.find((l) => l.id === listId);
    labels.push({
      id: listId,
      name: fromUnit?.name || fromCatalog?.name || unit.list_name || 'Lista',
    });
  });

  return labels;
};

/** Unión de listas matching para todas las unidades visibles del lote. */
export const getLotMatchingListLabels = (lot, listFilters, userLists = [], noListsValue = '__NO_LISTS__') => {
  const map = new Map();
  getDisplayUnits(lot).forEach((unit) => {
    getUnitMatchingListLabels(unit, listFilters, userLists, noListsValue).forEach((label) => {
      map.set(label.id, label);
    });
  });
  return Array.from(map.values());
};

/** Respuesta nueva: agrupada por lote con `units[]`. Compatibilidad con filas planas legadas. */
export const normalizeLotsData = (data) => {
  if (!Array.isArray(data)) return [];
  return data.map((row) => {
    if (Array.isArray(row.units)) {
      return {
        ...row,
        is_favorite: row.is_favorite === true,
        item_type: row.item_type || undefined,
        has_lot_content: row.has_lot_content === true || row.item_type === 'lot_content',
        latest_activity_at: row.latest_activity_at || null,
        unit_count: row.unit_count ?? row.units.length,
        units: row.units.map((unit) => ({
          ...unit,
          unit_label: toOptionalString(unit?.unit_label),
          title: toOptionalString(unit?.title),
          operation: mapOperationFromApi(unit) || unit.operation || null,
          official_unit: unit?.official_unit && typeof unit.official_unit === 'object'
            ? unit.official_unit
            : null,
        })),
      };
    }
    const unit = {
      id: row.id,
      lot_id: row.lot_id,
      unit_label: toOptionalString(row.unit_label),
      title: toOptionalString(row.title),
      description: row.description,
      observations: row.observations,
      is_public: row.is_public,
      price: row.price,
      currency: row.currency,
      operation: mapOperationFromApi(row) || row.operation || null,
      covered_surface: row.covered_surface,
      open_surface: row.open_surface,
      lists: row.lists,
      list_ids: row.list_ids,
      list_id: row.list_id,
      list_name: row.list_name,
      official_unit: row.official_unit && typeof row.official_unit === 'object'
        ? row.official_unit
        : null,
      created_at: row.created_at,
      updated_at: row.updated_at,
    };
    return {
      lot_id: row.lot_id,
      lot_name: row.lot_name,
      lot_state: row.lot_state,
      lot_type: row.lot_type,
      lot_surface: row.lot_surface,
      lot_urbansurface: row.lot_urbansurface,
      lot_valuation: row.lot_valuation,
      department_name: row.department_name,
      province_name: row.province_name,
      lat: row.lat,
      lng: row.lng,
      unit_count: 1,
      units: [unit],
    };
  });
};

export const lotMatchesSearch = (lot, term) => {
  const lotHaystack = [
    lot.lot_name,
    lot.department_name,
    lot.province_name,
    lot.lot_state,
    lot.lot_type,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (lotHaystack.includes(term)) return true;
  return getDisplayUnits(lot).some((u) => {
    const unitHaystack = [u.title, u.unit_label, u.description].filter(Boolean).join(' ').toLowerCase();
    return unitHaystack.includes(term);
  });
};

/** Agrupa filas planas de unidades (p. ej. lista compartida) por lote catastral. */
/** Rellena un campo faltante del grupo desde la fila (null/'' no pisan un valor ya seteado). */
const fillGroupField = (group, key, value) => {
  if (value === null || value === undefined) return;
  if (typeof value === 'string' && value.trim() === '') return;
  if (group[key] !== null && group[key] !== undefined && group[key] !== '') return;
  group[key] = value;
};

/**
 * Campos catastrales del lote que el listado shared ya manda (`lot_*` o canónicos
 * vía normalizeSharedLotsFromApi / filter_attributes). Hay que conservarlos al
 * reagrupar: si no, las cards de lote-solo quedan solo con tipo/estado.
 */
const fillGroupLotCadastralFields = (group, row) => {
  fillGroupField(group, 'lot_state', row.lot_state ?? row.state);
  fillGroupField(group, 'lot_type', row.lot_type ?? row.type);
  fillGroupField(group, 'lot_surface', row.lot_surface ?? row.surface);
  fillGroupField(group, 'lot_urbansurface', row.lot_urbansurface ?? row.urbansurface);
  fillGroupField(group, 'lot_valuation', row.lot_valuation ?? row.valuation);
  fillGroupField(group, 'state', row.state ?? row.lot_state);
  fillGroupField(group, 'type', row.type ?? row.lot_type);
  fillGroupField(group, 'surface', row.surface ?? row.lot_surface);
  fillGroupField(group, 'urbansurface', row.urbansurface ?? row.lot_urbansurface);
  fillGroupField(group, 'valuation', row.valuation ?? row.lot_valuation);
  fillGroupField(group, 'street_number', row.street_number);
  const attrs = row.filter_attributes && typeof row.filter_attributes === 'object'
    ? row.filter_attributes
    : null;
  if (attrs) {
    fillGroupField(group, 'lot_urbansurface', attrs.urbansurface ?? attrs.lot_urbansurface);
    fillGroupField(group, 'urbansurface', attrs.urbansurface ?? attrs.lot_urbansurface);
    fillGroupField(group, 'lot_surface', attrs.surface ?? attrs.lot_surface);
    fillGroupField(group, 'surface', attrs.surface ?? attrs.lot_surface);
    fillGroupField(group, 'lot_valuation', attrs.valuation ?? attrs.lot_valuation);
    fillGroupField(group, 'valuation', attrs.valuation ?? attrs.lot_valuation);
    fillGroupField(group, 'lot_type', attrs.type ?? attrs.lot_type);
    fillGroupField(group, 'type', attrs.type ?? attrs.lot_type);
    fillGroupField(group, 'lot_state', attrs.state ?? attrs.lot_state);
    fillGroupField(group, 'state', attrs.state ?? attrs.lot_state);
  }
};

export const groupUnitsByLot = (rows) => {
  if (!Array.isArray(rows) || !rows.length) return [];
  const map = new Map();
  rows.forEach((row) => {
    const lotId = row.lot_id;
    if (!lotId) return;
    if (!map.has(lotId)) {
      map.set(lotId, {
        lot_id: lotId,
        lot_name: row.lot_name,
        department_name: row.department_name,
        province_name: row.province_name,
        locality_name: row.locality_name ?? null,
        municipality_name: row.municipality_name ?? null,
        lot_state: row.lot_state ?? row.state ?? null,
        lot_type: row.lot_type ?? row.type ?? null,
        lot_surface: row.lot_surface ?? row.surface ?? null,
        lot_urbansurface: row.lot_urbansurface ?? row.urbansurface ?? null,
        lot_valuation: row.lot_valuation ?? row.valuation ?? null,
        state: row.state ?? row.lot_state ?? null,
        type: row.type ?? row.lot_type ?? null,
        surface: row.surface ?? row.lot_surface ?? null,
        urbansurface: row.urbansurface ?? row.lot_urbansurface ?? null,
        valuation: row.valuation ?? row.lot_valuation ?? null,
        lat: row.lat ?? null,
        lng: row.lng ?? null,
        boundary: row.boundary ?? null,
        item_type: row.item_type || null,
        lot_user_content: row.lot_user_content != null ? row.lot_user_content : null,
        filter_attributes: row.filter_attributes ?? null,
        neighborhood_name: row.neighborhood_name ?? null,
        street_name: row.street_name ?? row.lot_street ?? null,
        street_number: row.street_number ?? null,
        ...pickLotContextFields(row),
        unit_count: 0,
        units: [],
      });
      fillGroupLotCadastralFields(map.get(lotId), row);
    }
    const group = map.get(lotId);
    if (!group.lot_name && row.lot_name) group.lot_name = row.lot_name;
    if (!group.locality_name && row.locality_name) group.locality_name = row.locality_name;
    if (!group.municipality_name && row.municipality_name) group.municipality_name = row.municipality_name;
    if (group.lat == null && row.lat != null) group.lat = row.lat;
    if (group.lng == null && row.lng != null) group.lng = row.lng;
    if (!group.boundary && row.boundary) group.boundary = row.boundary;
    if (row.lot_user_content != null) group.lot_user_content = row.lot_user_content;
    if (!group.filter_attributes && row.filter_attributes) {
      group.filter_attributes = row.filter_attributes;
    }
    if (!group.neighborhood_name && row.neighborhood_name) {
      group.neighborhood_name = row.neighborhood_name;
    }
    if (!group.street_name && (row.street_name || row.lot_street)) {
      group.street_name = row.street_name || row.lot_street;
    }
    fillGroupLotCadastralFields(group, row);

    const isDirectLotRow = row.item_type === 'lot'
      || (row.user_lot_id == null && row.id == null);
    if (isDirectLotRow) {
      group.item_type = 'lot';
      return;
    }

    group.units.push({
      ...row,
      user_lot_id: row.user_lot_id ?? row.id,
    });
  });
  return Array.from(map.values()).map((g) => ({
    ...g,
    unit_count: g.units.length,
    item_type: g.units.length === 0 ? (g.item_type || 'lot') : undefined,
  }));
};
